import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, finalize, from, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getBackOfficeRuntimeEnv } from '../config/backoffice-runtime-env';
import { AuthSessionService } from '../services/auth-session.service';
import { AuthService } from '../services/auth.service';

let authorizationFailureHandled = false;
const API_BASE_URL_STORAGE_KEY = 'ccapp.bo.api-base-url.v2';
const API_BASE_URL_ATTEMPT = new HttpContextToken<number>(() => 0);
const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);
const AUTH_REAUTHENTICATION_RETRIED = new HttpContextToken<boolean>(() => false);
const ADMIN_AUTH_PATH_PREFIX = '/api/admin/auth/';

/**
 * Opt-in : un 403 sur cette requête est un refus métier que la page affiche
 * (ex. `GEN1_HELP_AGENT_NOT_LINKED`), pas une session perdue. Par défaut faux :
 * toute autre requête garde la déconnexion sur 403. Le 401 n'est jamais concerné.
 */
export const FORBIDDEN_IS_A_REFUSAL = new HttpContextToken<boolean>(() => false);

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function isProductionBackend(url: string): boolean {
  try {
    return /(^|[.-])prod([.-]|$)/i.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

function getBackendBaseUrl(): string {
  const runtimeUrl = getBackOfficeRuntimeEnv().BACKEND_BASE_URL;
  const backendBaseUrl = normalizeBaseUrl(runtimeUrl ?? '');
  if (!backendBaseUrl) {
    throw new Error('BACKEND_BASE_URL runtime config is missing. Check public/env.js generation.');
  }
  if (!environment.production && isProductionBackend(backendBaseUrl)) {
    console.warn('Blocked development backoffice call to production backend', backendBaseUrl);
    throw new Error(`Development backoffice cannot call production backend: ${backendBaseUrl}`);
  }
  return backendBaseUrl;
}

function getApiBaseUrls(): string[] {
  return Array.from(new Set([
    getBackendBaseUrl(),
    ...(environment.apiBaseUrlFallbacks ?? []),
  ].map((url) => normalizeBaseUrl(url)).filter((url) => url.length > 0)));
}

function getOrderedApiBaseUrls(): string[] {
  const apiBaseUrls = getApiBaseUrls();

  try {
    const storedBaseUrl = normalizeBaseUrl(localStorage.getItem(API_BASE_URL_STORAGE_KEY) ?? '');
    const storedIndex = apiBaseUrls.indexOf(storedBaseUrl);

    if (storedIndex > 0) {
      return [
        apiBaseUrls[storedIndex],
        ...apiBaseUrls.filter((_, index) => index !== storedIndex),
      ];
    }
  } catch {
    return apiBaseUrls;
  }

  return apiBaseUrls;
}

function rememberApiBaseUrl(apiBaseUrl: string): void {
  try {
    localStorage.setItem(API_BASE_URL_STORAGE_KEY, apiBaseUrl);
  } catch {
    // Ignore storage failures and keep the request flow moving.
  }
}

function isRetryableGatewayError(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse
    && [0, 404, 502, 503, 504].includes(error.status);
}

function sessionRecoveryError(url: string | null | undefined): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 440,
    statusText: 'Session recovery required',
    url: url ?? undefined,
    error: {
      success: false,
      message: 'Session recovery required',
      data: null,
    },
  });
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const authSessionService = inject(AuthSessionService, { optional: true });
  const isRelativeRequest = request.url.startsWith('/');
  const shouldPreferPrimaryBaseUrl = isRelativeRequest && request.url.startsWith(ADMIN_AUTH_PATH_PREFIX);
  const shouldSkipAuthorizationHeader = isRelativeRequest && request.url.startsWith(ADMIN_AUTH_PATH_PREFIX);
  const tracksUserSession = isRelativeRequest && !shouldSkipAuthorizationHeader;
  const longOperationId = tracksUserSession
    ? authSessionService?.beginLongOperation(request.url)
    : null;
  const apiBaseUrls = isRelativeRequest
    ? (shouldPreferPrimaryBaseUrl ? getApiBaseUrls() : getOrderedApiBaseUrls())
    : [];

  if (authService.hasAuthState()) {
    authorizationFailureHandled = false;
  }

  const handleAuthorizationFailure = (error: unknown): void => {
    if (
      error instanceof HttpErrorResponse
      && (error.status === 401 || error.status === 403)
      && !(error.status === 403 && request.context.get(FORBIDDEN_IS_A_REFUSAL))
      && authService.hasAuthState()
      && !authorizationFailureHandled
    ) {
      authorizationFailureHandled = true;
      authService.handleAuthorizationFailure();
    }
  };

  const createRequest = (attempt: number) => {
    const url = isRelativeRequest && apiBaseUrls[attempt]
      ? `${apiBaseUrls[attempt]}${request.url}`
      : request.url;
    const currentToken = authService.token();

    return currentToken && !shouldSkipAuthorizationHeader
      ? request.clone({
          url,
          context: request.context.set(API_BASE_URL_ATTEMPT, attempt),
          setHeaders: {
            Authorization: `Bearer ${currentToken}`,
          },
        })
      : request.clone({
          url,
          context: request.context.set(API_BASE_URL_ATTEMPT, attempt),
        });
  };

  const replayWithCurrentToken = (attempt: number, markReauthenticated: boolean): Observable<HttpEvent<unknown>> => {
    const refreshedToken = authService.token();
    const retryUrl = isRelativeRequest && apiBaseUrls[attempt]
      ? `${apiBaseUrls[attempt]}${request.url}`
      : request.url;
    const retryContext = request.context
      .set(API_BASE_URL_ATTEMPT, attempt)
      .set(AUTH_REFRESH_RETRIED, true)
      .set(AUTH_REAUTHENTICATION_RETRIED, markReauthenticated || request.context.get(AUTH_REAUTHENTICATION_RETRIED));
    const retriedRequest = refreshedToken
      ? request.clone({
          url: retryUrl,
          context: retryContext,
          setHeaders: {
            Authorization: `Bearer ${refreshedToken}`,
          },
        })
      : request.clone({
          url: retryUrl,
          context: retryContext,
        });

    return next(retriedRequest).pipe(
      tap(() => {
        if (isRelativeRequest && apiBaseUrls[attempt]) {
          rememberApiBaseUrl(apiBaseUrls[attempt]);
        }
      }),
      catchError((retryError: unknown) => {
        if (
          retryError instanceof HttpErrorResponse
          && retryError.status === 401
          && authService.hasAuthState()
          && authSessionService
          && !markReauthenticated
          && !request.context.get(AUTH_REAUTHENTICATION_RETRIED)
        ) {
          return reauthenticateAndReplay(retryError, attempt);
        }

        handleAuthorizationFailure(retryError);
        return retryError instanceof HttpErrorResponse && retryError.status === 401
          ? throwError(() => sessionRecoveryError(retryError.url))
          : throwError(() => retryError);
      }),
    );
  };

  const reauthenticateAndReplay = (error: HttpErrorResponse, attempt: number): Observable<HttpEvent<unknown>> => {
    if (!authSessionService || request.context.get(AUTH_REAUTHENTICATION_RETRIED)) {
      handleAuthorizationFailure(error);
      return throwError(() => sessionRecoveryError(error.url));
    }

    return from(authSessionService.requestReauthentication('http-401')).pipe(
      switchMap((reauthenticated) => {
        if (!reauthenticated || !authService.token()) {
          return throwError(() => sessionRecoveryError(error.url));
        }

        return replayWithCurrentToken(attempt, true);
      }),
    );
  };

  const dispatch = (attempt: number): Observable<HttpEvent<unknown>> => next(createRequest(attempt)).pipe(
    tap(() => {
      if (isRelativeRequest && apiBaseUrls[attempt]) {
        rememberApiBaseUrl(apiBaseUrls[attempt]);
      }
    }),
    catchError((error: unknown) => {
      if (isRelativeRequest && isRetryableGatewayError(error) && attempt + 1 < apiBaseUrls.length) {
        return dispatch(attempt + 1);
      }

      if (
        isRelativeRequest
        && !shouldSkipAuthorizationHeader
        && error instanceof HttpErrorResponse
        && error.status === 401
        && authService.hasAuthState()
        && !request.context.get(AUTH_REFRESH_RETRIED)
      ) {
        const refreshBaseUrl = apiBaseUrls[attempt] ?? getBackendBaseUrl();
        return from(authService.refreshAccessToken(refreshBaseUrl)).pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              return reauthenticateAndReplay(error, attempt);
            }

            return replayWithCurrentToken(attempt, false);
          }),
        );
      }

      if (
        isRelativeRequest
        && !shouldSkipAuthorizationHeader
        && error instanceof HttpErrorResponse
        && error.status === 401
        && authService.hasAuthState()
        && authSessionService
        && !request.context.get(AUTH_REAUTHENTICATION_RETRIED)
      ) {
        return reauthenticateAndReplay(error, attempt);
      }

      handleAuthorizationFailure(error);
      return error instanceof HttpErrorResponse && error.status === 401
        ? throwError(() => sessionRecoveryError(error.url))
        : throwError(() => error);
    }),
  );

  return dispatch(request.context.get(API_BASE_URL_ATTEMPT)).pipe(
    finalize(() => authSessionService?.endLongOperation(longOperationId)),
  );
};
